import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorBox } from '../components/Feedback.js';
import {
  passkeySuportado,
  useAuth,
  useEntrarComPasskey,
  useEsqueciSenha,
  useLogin,
  useRegister,
} from '../api/queries.js';

/**
 * Login e criação de conta na mesma tela.
 *
 * Quando ainda não existe nenhuma conta no sistema, a tela abre direto no modo
 * "criar conta" — não faz sentido pedir senha de uma conta que não existe.
 */
export function Login() {
  const { data: auth } = useAuth();
  const login = useLogin();
  const registrar = useRegister();

  const semContas = auth?.setupRequired === true;

  /**
   * O link do convite já traz o código e o e-mail — no fragmento, não na query.
   *
   * Quem recebeu o convite não tem por onde adivinhar esta tela: com o cadastro
   * fechado, o botão "criar conta" nem aparece. O link resolve isso e ainda
   * evita a digitação de um código de oito caracteres no celular.
   *
   * **Lê depois do `#` de propósito.** O fragmento nunca sai do navegador: não
   * vai no log do nginx, não vai para o OpenSearch, não é visto pela
   * Cloudflare. Na query, o código do convite andaria em texto puro por todos
   * esses lugares — anulando o hash SHA-256 que o protege no banco. Ver
   * `backend/src/services/mensagemDoConvite.ts`.
   */
  const esqueci = useEsqueciSenha();
  const entrarComDigital = useEntrarComPasskey();

  /*
   * Quem acabou de redefinir a senha chega aqui com este aviso no `state` da
   * navegação, e não numa tela de confirmação própria: uma batida a menos, e a
   * pessoa cai exatamente onde precisa agir.
   *
   * Dizer que os outros aparelhos caíram não é detalhe técnico. Se ela pediu
   * recuperação por desconfiar de invasão, é esta frase que responde a pergunta
   * dela — e é a única chance de dizer isso, porque a tela de onde ela veio já
   * não existe mais.
   */
  const { hash, state } = useLocation();
  const senhaRedefinida = (state as { senhaRedefinida?: boolean } | null)?.senhaRedefinida === true;

  const doLink = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const conviteDaUrl = doLink.get('convite') ?? '';

  const [modoEscolhido, setModo] = useState<'login' | 'registro' | 'esqueci' | null>(
    conviteDaUrl ? 'registro' : null,
  );
  // Instalação nova abre direto em "criar conta"; a escolha manual prevalece.
  const modo = modoEscolhido ?? (semContas ? 'registro' : 'login');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState(doLink.get('email') ?? '');
  const [senha, setSenha] = useState('');
  const [convite, setConvite] = useState(conviteDaUrl);

  const criandoConta = modo === 'registro';
  /*
   * "Esqueci" é um terceiro modo desta mesma tela, e não outra rota, porque a
   * pessoa chega aqui tendo acabado de digitar o e-mail e errar a senha. Navegar
   * para outro lugar perderia o e-mail e a faria digitar de novo, justamente
   * quando ela já está irritada.
   */
  const esqueceu = modo === 'esqueci';
  /** Com o cadastro fechado e contas já existindo, só se entra por convite. */
  const precisaDeConvite = criandoConta && !semContas && auth?.registrationOpen === false;
  const pendente = login.isPending || registrar.isPending || esqueci.isPending;
  const erro = login.error ?? registrar.error ?? esqueci.error ?? entrarComDigital.error;

  const enviar = async () => {
    if (esqueceu) {
      await esqueci.mutateAsync(email.trim());
    } else if (criandoConta) {
      await registrar.mutateAsync({
        name: nome.trim(),
        email: email.trim(),
        password: senha,
        // Vazio vira `undefined`: o schema trata o campo como ausente, e a
        // primeira conta da instalação continua entrando sem convite nenhum.
        inviteCode: convite.trim() || undefined,
      });
    } else {
      await login.mutateAsync({ email: email.trim(), password: senha });
    }
  };

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-5 py-10">
      <header className="mb-6 text-center">
        <p aria-hidden className="text-5xl">
          🍷
        </p>
        <h1 className="mt-3 text-2xl font-semibold">Minha Adega</h1>
        <p className="mt-1 text-sm text-adega-muted">
          {esqueceu
            ? 'Informe seu e-mail e mandamos um link para você criar uma senha nova.'
            : criandoConta
              ? 'Crie sua conta para começar a cadastrar suas garrafas.'
              : 'Entre para ver sua adega.'}
        </p>
      </header>

      {senhaRedefinida ? (
        <p className="mb-4 rounded-xl bg-adega-surface-2 p-3 text-center text-sm leading-relaxed">
          Senha redefinida. Entre com a senha nova — todos os outros aparelhos foram desconectados.
        </p>
      ) : null}

      <form
        className="card space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void enviar();
        }}
      >
        {criandoConta ? (
          <div>
            <label className="label" htmlFor="nome">
              Seu nome
            </label>
            <input
              id="nome"
              className="field"
              autoComplete="name"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Como quer ser chamado"
            />
          </div>
        ) : null}

        <div>
          <label className="label" htmlFor="email">
            E-mail
          </label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            className="field"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="voce@exemplo.com"
          />
        </div>

        {esqueceu ? null : (
        <div>
          <label className="label" htmlFor="senha">
            Senha
          </label>
          <input
            id="senha"
            type="password"
            autoComplete={criandoConta ? 'new-password' : 'current-password'}
            className="field"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            placeholder={criandoConta ? 'Pelo menos 8 caracteres' : 'Sua senha'}
          />
        </div>
        )}

        {erro ? <ErrorBox erro={erro} /> : null}

        {/* A mensagem é a mesma exista ou não a conta: o servidor responde
            igual nos dois casos, e a tela não pode contar o que ele calou.
            Quem digitou o e-mail errado espera um e-mail que não vem — é o
            preço de não entregar quem tem conta aqui. */}
        {esqueci.isSuccess ? (
          <p className="rounded-xl bg-adega-surface-2 p-3 text-sm leading-relaxed">
            Se existe uma conta com esse e-mail, enviamos um link para redefinir a senha. Ele vale
            por uma hora.
          </p>
        ) : null}

        {precisaDeConvite ? (
          <div>
            <label htmlFor="convite" className="text-sm text-adega-muted">
              Código do convite
            </label>
            <input
              id="convite"
              className="input mt-1 w-full text-center font-mono text-lg tracking-[0.3em] uppercase"
              autoComplete="off"
              autoCapitalize="characters"
              placeholder="XXXXXXXX"
              value={convite}
              onChange={(evento) => setConvite(evento.target.value)}
            />
            <p className="mt-1 text-xs text-adega-muted">
              O cadastro é por convite. Use o código que o dono do sistema te passou, no mesmo
              e-mail que ele cadastrou.
            </p>
          </div>
        ) : null}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={
            pendente ||
            email.trim().length === 0 ||
            (!esqueceu && senha.length === 0) ||
            (precisaDeConvite && convite.trim().length === 0)
          }
        >
          {pendente
            ? 'Aguarde…'
            : esqueceu
              ? 'Enviar link'
              : criandoConta
                ? 'Criar conta'
                : 'Entrar'}
        </button>

        {/*
          Entrar com a digital.
          
          Só no login, e só onde o navegador suporta WebAuthn -- que exige
          contexto seguro: https ou localhost, nunca http://192.168.0.x. Sumir
          é melhor que um botão que dá erro ao ser tocado.
          
          Não pede e-mail antes: a credencial é descobrível, então o aparelho
          mostra as contas que guarda e a pessoa escolhe. Perguntar quem ela é
          primeiro seria metade do atrito que este botão existe para tirar.
        */}
        {modo === 'login' && passkeySuportado() ? (
          <button
            type="button"
            className="btn-secondary w-full"
            disabled={entrarComDigital.isPending}
            onClick={() => void entrarComDigital.mutateAsync()}
          >
            {entrarComDigital.isPending ? 'Aguarde…' : '👆 Entrar com a digital'}
          </button>
        ) : null}

        {/* Só no login: em "criar conta" não há senha a esquecer, e dentro do
            próprio "esqueci" o caminho de volta é o botão abaixo. */}
        {modo === 'login' ? (
          <button
            type="button"
            className="btn-ghost w-full text-xs"
            onClick={() => setModo('esqueci')}
          >
            Esqueci minha senha
          </button>
        ) : null}

        {esqueceu ? (
          <button
            type="button"
            className="btn-ghost w-full text-xs"
            onClick={() => setModo('login')}
          >
            Voltar para a entrada
          </button>
        ) : null}

        {/* Com o cadastro fechado o texto muda, mas o caminho continua existindo:
            sem ele, quem recebeu um convite por WhatsApp (sem o link) ficaria
            olhando para uma tela de login sem saída. */}
        {!semContas && !esqueceu ? (
          <button
            type="button"
            className="btn-ghost w-full text-xs"
            onClick={() => setModo(criandoConta ? 'login' : 'registro')}
          >
            {criandoConta
              ? 'Já tenho conta'
              : auth?.registrationOpen
                ? 'Criar uma conta'
                : 'Tenho um convite'}
          </button>
        ) : null}

        {semContas ? (
          <p className="text-center text-xs text-adega-muted">
            Primeira vez por aqui: a conta criada agora fica com as adegas já cadastradas.
          </p>
        ) : null}
      </form>

      <p className="mt-6 text-center text-xs text-adega-muted">
        A sessão fica ativa por semanas, para você não digitar senha toda vez que
        estiver na frente da adega.
      </p>
    </div>
  );
}
